"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var log_1 = require("../utils/log");
var utils_1 = require("../utils/utils");
var AuctionHandler_1 = require("./AuctionHandler");
var Bid_1 = require("./Bid");
var ViewabilityTracker_1 = require("./ViewabilityTracker");
var Page_1 = require("./Page");
var VideoInstance_1 = require("./VideoInstance");
var Slot = /** @class */ (function () {
    /**
     * @param slotConfig
     * @description Slot constructor
     */
    function Slot(slotConfig) {
        this.sizes = [];
        this.min_height = 0;
        this.floor = 0.10;
        this.bids = [];
        this.auction_finished = false;
        this.ad_injected = false;
        this.displayed = false;
        this.displayed_ts = 0;
        this.refresh = {
            enabled: false,
            interval: 15000,
            interval_met: false,
            interval_timer: null,
            current_interval: 0,
            inview_interval: 5000,
            inview_interval_met: false,
            inview_interval_timer: null,
            current_inview_interval: 0,
            count: 0
        };
        this.viewability = {
            enabled: true,
            inview: null,
            active_tab: true,
            entered_inview_ts: 0,
            time_inview_since_displayed: 0
        };
        this.lazyload = {
            enabled: false,
            fetch_threshold: 100,
            in_fetch_range: false,
            display_threshold: 50,
            in_display_range: false
        };
        this.auto_delete = {
            enabled: false,
            threshold: 100
        };
        this.config = slotConfig;
        this.id = slotConfig['id'];
        this.name = slotConfig['name'];
        this.floor = slotConfig['floor'];
        this.sticky = slotConfig['sticky'];
        this.div_id = "bigcrunch-unit-" + this.name;
        this.css_seselector = slotConfig['css_seselector'] || '';
        this.referenceElement = slotConfig['referenceElement'];
        Object.assign(this.lazyload, slotConfig['lazyload']);
        Object.assign(this.refresh, slotConfig['refresh']);
        Object.assign(this.auto_delete, slotConfig['auto_delete']);
    }
    Slot.prototype.buildSlot = function () {
        // Set sizes for slot
        this.sizes = this.setSizes();
        // Set min height for slot
        this.getMinHeight();
        // Insert slot into dom
        if (!this.insertSlot()) {
            (0, log_1.logWarn)("Unable to insert slot into dom.", this.name);
            return false;
        }
        // set viewability tracker
        this.setViewabilityTracker();
        // check lazyload is enabled
        if (this.lazyload.enabled) {
            this.setLazyLoadTrackers();
        }
        else {
            this.lazyload.in_fetch_range = true;
            this.lazyload.in_display_range = true;
            this.sendNewAuction();
        }
        // set auto delete tracker
        this.setAutoDeleteTracker();
        return true;
    };
    Slot.prototype.setViewabilityTracker = function () {
        var _this = this;
        if (this.viewability.enabled) {
            // track element viewability
            ViewabilityTracker_1.viewabilityTracker.trackElement(this.element, function (entry) {
                _this.handleViewabilityChange(entry.isIntersecting);
            });
        }
    };
    Slot.prototype.removeViewabilityTracker = function () {
        if (this.viewability.enabled) {
            // track element viewability
            ViewabilityTracker_1.viewabilityTracker.untrackElement(this.element);
        }
    };
    Slot.prototype.setLazyLoadTrackers = function () {
        var _this = this;
        if (this.lazyload.enabled) {
            // set lazyload fetch threshold if it is less than display threshold
            if (this.lazyload.fetch_threshold < this.lazyload.display_threshold) {
                this.lazyload.fetch_threshold = this.lazyload.display_threshold;
            }
            // set lazyload fetch observer
            var lazyload_fetch_options = {
                root: null,
                rootMargin: this.lazyload.fetch_threshold + '%',
                threshold: 0.5
            };
            this.lazyloadFetchObserver = new ViewabilityTracker_1.ViewabilityTracker(false, lazyload_fetch_options);
            this.lazyloadFetchObserver.trackElement(this.element, function (entry) {
                if (_this.lazyload.fetch_threshold !== _this.lazyload.display_threshold) {
                    _this.handleLazyLoadChange('fetch', entry);
                }
                else {
                    _this.handleLazyLoadChange('fetch', entry);
                    _this.handleLazyLoadChange('display', entry);
                }
            });
            if (this.lazyload.fetch_threshold !== this.lazyload.display_threshold) {
                // set lazyload display observer
                var lazyload_display_options = {
                    root: null,
                    rootMargin: this.lazyload.display_threshold + '%',
                    threshold: 0.5
                };
                this.lazyloaddisplayObserver = new ViewabilityTracker_1.ViewabilityTracker(false, lazyload_display_options);
                this.lazyloaddisplayObserver.trackElement(this.element, function (entry) {
                    _this.handleLazyLoadChange('display', entry);
                });
            }
        }
    };
    Slot.prototype.removeLazyloadTrackers = function () {
        if (this.lazyloadFetchObserver) {
            // remove lazyload fetch observer
            this.lazyloadFetchObserver.untrackElement(this.element);
        }
        if (this.lazyloaddisplayObserver) {
            // remove lazyload display observer
            this.lazyloaddisplayObserver.untrackElement(this.element);
        }
    };
    Slot.prototype.setAutoDeleteTracker = function () {
        var _this = this;
        if (this.auto_delete.enabled) {
            // set lazyload fetch observer
            var auto_delete_options = {
                root: null,
                rootMargin: this.auto_delete.threshold + '%',
                threshold: 0.5
            };
            this.autoDeleteObserver = new ViewabilityTracker_1.ViewabilityTracker(false, auto_delete_options);
            this.autoDeleteObserver.trackElement(this.element, function (entry) {
                var boundingClientRect = entry.boundingClientRect, rootBounds = entry.rootBounds;
                // Check if the element is above the viewport
                if (boundingClientRect.bottom <= rootBounds.top) {
                    // console.log('Element is above the viewport.');
                    _this.removeSlot();
                }
            });
        }
    };
    Slot.prototype.removeAutoDeleteTracker = function () {
        if (this.autoDeleteObserver) {
            // remove lazyload fetch observer
            this.autoDeleteObserver.untrackElement(this.element);
        }
    };
    Slot.prototype.handleLazyLoadChange = function (threshold_type, entry) {
        var isIntersecting = entry.isIntersecting;
        if (threshold_type === 'fetch') {
            // Check if lazyload fetch has changed
            if (this.lazyload.in_fetch_range === isIntersecting)
                return;
            this.lazyload.in_fetch_range = isIntersecting;
            if ((!this.auction &&
                !this.auction_finished &&
                this.refresh.count == 0) ||
                this.checkIfRefreshIsAllowed()) {
                this.sendNewAuction();
            }
        }
        else if (threshold_type === 'display') {
            if (this.lazyload.in_display_range === isIntersecting)
                return;
            this.lazyload.in_display_range = isIntersecting;
            // check if slot has winning bid
            if (this.checkIfShouldDisplayWinningBid()) {
                // display winning bid
                this.displayWinningBid();
            }
        }
    };
    Slot.prototype.sendNewAuction = function () {
        // check if an auction has already been sent
        if (!this.auction) {
            (0, log_1.logInfo)("sendNewAuction", this.name, this.sizes);
            // set auction finished to false
            this.auction_finished = false;
            // add slot to auction
            this.auction = AuctionHandler_1.auctionHandler.addSlotsToAuction([this]);
            // send auction request. Allow 100 ms to queue up other slots
            AuctionHandler_1.auctionHandler.sendAuctionRequest(100);
        }
    };
    /**
     * Insert slot into element
     * @returns boolean
     */
    Slot.prototype.insertSlot = function () {
        try {
            this.referenceElement.classList.add('bigcrunch-unit-injected');
            // insert div inside wrapper div
            var inner_div = document.createElement('div');
            // add id to div
            inner_div.id = this.div_id;
            inner_div.classList.add('bigcrunch-unit');
            if (!this.sticky.enabled) {
                // set min height for wrapper div
                inner_div.style.minHeight = "".concat(this.min_height, "px");
            }
            else {
                inner_div.style.position = 'fixed';
                inner_div.classList.add('bigcrunch-unit-sticky');
            }
            this.referenceElement.appendChild(inner_div);
            // set element
            this.element = inner_div;
            return true;
        }
        catch (error) {
            return false;
        }
    };
    /**
     * @returns Array<string>
     * @description Set sizes for slot
     */
    Slot.prototype.setSizes = function () {
        try {
            var resolutionSizes = this.config['resolutionSizes'];
            var sizes = [];
            var current_resolution = 0;
            var page_width = (0, utils_1.getPageWidth)();
            // loop through resolution sizes
            for (var resolution in resolutionSizes) {
                if (parseInt(resolution) <= page_width &&
                    parseInt(resolution) >= current_resolution) {
                    current_resolution = parseInt(resolution);
                    sizes = resolutionSizes[resolution];
                }
            }
            return sizes;
        }
        catch (error) {
            (0, log_1.logWarn)("Unable to set sizes for slot.", this.name);
            return [];
        }
    };
    Slot.prototype.getMinHeight = function () {
        if (this.sizes.length) {
            // loop through sizes
            for (var _i = 0, _a = this.sizes; _i < _a.length; _i++) {
                var size = _a[_i];
                // get height
                var height = parseInt(size.split('x')[1]);
                // check if height is greater than min_height
                if (height < this.min_height || this.min_height == 0) {
                    this.min_height = height;
                }
            }
        }
    };
    Slot.prototype.checkIfShouldDisplayWinningBid = function () {
        // check if slot has winning bid
        return ((( // First impession
        this.ad_injected === false &&
            this.displayed === false &&
            this.refresh.count == 0) ||
            ( // Refresh impression
            this.refresh.enabled &&
                this.refresh.count > 0 &&
                this.refresh.interval_met &&
                this.refresh.inview_interval_met)) &&
            ( // Lazyload requirements are met
            !this.lazyload.enabled ||
                this.lazyload.in_display_range) &&
            !this.auction &&
            this.auction_finished &&
            Page_1.globalPage.isTabActive);
    };
    Slot.prototype.auctionFinished = function (bids) {
        this.addBids(bids);
        // set auction finished to true
        this.auction = null;
        this.auction_finished = true;
        // check if slot has winning bid
        if (this.checkIfShouldDisplayWinningBid()) {
            // display winning bid
            this.displayWinningBid();
        }
    };
    /**
     * @param {Array<Bid>} bids
     * @returns void
     * @description Add bids to slot
     */
    Slot.prototype.addBids = function (bids) {
        var _a;
        // add bids to slot
        (_a = this.bids).push.apply(_a, bids);
        // sort bids by price from highest to lowest
        this.bids.sort(function (a, b) {
            return b.data.price - a.data.price;
        });
        // remove expired bids
        if (this.bids.length) {
            // remove expired bids
            this.removeExpiredBids();
        }
    };
    /**
     * @returns boolean
     */
    Slot.prototype.getWinningBid = function () {
        if (this.bids.length) {
            var min_bid_to_win = this.getMinBidToWin();
            this.winningBid = this.bids.shift() || null;
            if (min_bid_to_win) {
                this.winningBid.min_bid_to_win = min_bid_to_win;
            }
        }
        return this.hasWinningBid();
    };
    /**
     * @returns number
     * @description Get minimum bid to win
     */
    Slot.prototype.getMinBidToWin = function () {
        if (this.bids.length > 1) {
            return this.bids[1].data.price;
        }
        return 0;
    };
    /**
     * @returns boolean
     */
    Slot.prototype.hasWinningBid = function () {
        return this.winningBid instanceof Bid_1.Bid;
    };
    /**
     * @returns boolean
     * @description Display winning bid
     */
    Slot.prototype.displayWinningBid = function () {
        // check if slot has winning bid
        if (this.getWinningBid()) {
            // check if lazyload is enabled and slot is not in display range
            if (this.lazyload.enabled &&
                this.lazyload.in_display_range === false)
                return false;
            // set displayed data
            this.ad_injected = true;
            this.displayedBid = this.winningBid;
            this.winningBid = null;
            // check if winning bid is a video
            if (this.displayedBid.demand_type === 'video') {
                this.insertOutstreamPlayer();
                // check if winning bid is a display
            }
            else if (this.displayedBid.demand_type === 'display') {
                this.injectAdCode(this.displayedBid.data);
            }
        }
        else if (!this.ad_injected &&
            !this.displayed &&
            this.refresh.count == 0) {
            (0, log_1.logInfo)("No bids for slot. Fallback to sticky", this.name);
            // insert sticky ad document.write("<div id=\"sticky_ad_container\" class=\"htlad-snopes_dt_sticky\" data-targeting='{\"sticky_refresh\": \"01\"}'></div>");
            var sticky_ad_container = document.createElement('div');
            sticky_ad_container.id = 'sticky_ad_container';
            sticky_ad_container.classList.add('htlad-snopes_dt_sticky');
            sticky_ad_container.setAttribute('data-targeting', '{"sticky_refresh": "01"}');
            // append to body
            document.body.appendChild(sticky_ad_container);
        }
        // Refresh slot
        if (this.refresh.enabled) {
            this.resetRefresh();
            this.resetViewability();
            this.setRefreshTimer();
            this.setInviewRefreshTimer();
        }
        return true;
    };
    // function to put ad code into an iframe and display it in the slots element with the div_id
    // @param {string} adCode
    // @returns {boolean}
    Slot.prototype.injectAdCode = function (ad) {
        try {
            // create iframe
            var iframe = document.createElement('iframe');
            // set iframe id
            iframe.id = this.div_id;
            // set iframe srcdoc
            iframe.srcdoc = ad.adm;
            // set iframe style
            iframe.style.width = ad.w;
            iframe.style.height = ad.h;
            // insert iframe into element
            this.element.appendChild(iframe);
            return true;
        }
        catch (error) {
            return false;
        }
    };
    /**
     * @returns void
     * @description Reset refresh
     */
    Slot.prototype.resetRefresh = function () {
        // Reset Flags
        this.refresh.interval_met = false;
        this.refresh.inview_interval_met = false;
        // Clear Timeouts
        clearTimeout(this.refresh.interval_timer);
        this.refresh.interval_timer = null;
        clearTimeout(this.refresh.inview_interval_timer);
        this.refresh.inview_interval_timer = null;
        // Reset Intervals
        this.refresh.current_interval = this.refresh.interval;
        this.refresh.current_inview_interval = this.refresh.inview_interval;
    };
    Slot.prototype.resetViewability = function () {
        // Reset Viewability Data
        this.viewability.time_inview_since_displayed = 0;
        var isInView = this.viewability.inview;
        this.viewability.inview = null;
        this.handleViewabilityChange(isInView);
    };
    /**
     * @returns boolean
     * @description Check if refresh is allowed
     */
    Slot.prototype.checkIfRefreshIsAllowed = function () {
        if (!this.auction &&
            this.displayed &&
            !this.videoPlayer &&
            this.refresh.enabled &&
            this.refresh.interval_met &&
            this.refresh.inview_interval_met &&
            (!this.lazyload.enabled ||
                this.lazyload.in_fetch_range)) {
            return true;
        }
        return false;
    };
    // function to set displayed flag
    Slot.prototype.setDisplayed = function () {
        this.displayed = true;
        this.displayed_ts = Date.now();
        // If slot is inview
        if (this.viewability.inview) {
            // Set entered inview timestamp
            this.viewability.entered_inview_ts = Date.now();
            // set refresh timer
            this.setRefreshTimer();
            // set inview refresh timer if allowed
            this.setInviewRefreshTimer();
            // set viewability timer
            this.displayedBid.setViewabilityTimer();
        }
    };
    /**
     * @returns boolean
     * @description Refresh slot
     */
    Slot.prototype.refreshSlot = function () {
        if (this.checkIfRefreshIsAllowed()) {
            this.sendNewAuction();
            this.refresh.count++;
        }
        return true;
    };
    /**
     * @returns boolean
     * @description Refresh slot
     */
    Slot.prototype.setRefreshTimer = function () {
        var _this = this;
        // check if refresh is enabled
        if (this.refresh.interval_timer !== null ||
            this.refresh.interval_met ||
            !this.refresh.enabled ||
            !this.displayed) {
            return false;
        }
        // Clear refresh timer if it exists
        if (this.refresh.interval_timer !== null) {
            clearTimeout(this.refresh.interval_timer);
            this.refresh.interval_timer = null;
        }
        // Get time since slot was displayed
        var time_since_displayed = Date.now() - this.displayed_ts;
        // Set current interval
        this.refresh.current_interval = Math.max(this.refresh.interval - time_since_displayed, 0);
        if (this.refresh.current_interval > 0) {
            // set refresh timer
            this.refresh.interval_timer = setTimeout(function () {
                if (!_this.refresh.interval_met) {
                    _this.setRefreshIntervalMet();
                }
            }, this.refresh.current_interval);
        }
        else {
            if (!this.refresh.interval_met) {
                this.setRefreshIntervalMet();
            }
        }
        return true;
    };
    Slot.prototype.setRefreshIntervalMet = function () {
        this.refresh.interval_met = true;
        if (this.checkIfRefreshIsAllowed()) {
            // refresh slot
            this.refreshSlot();
        }
    };
    Slot.prototype.setInviewRefreshTimer = function () {
        var _this = this;
        if (!this.displayed ||
            !this.refresh.enabled ||
            !this.viewability.inview ||
            this.refresh.inview_interval_met ||
            this.refresh.inview_interval_timer !== null) {
            return true;
        }
        // check if inview refresh is enabled
        if (this.refresh.inview_interval == -1) {
            this.setInviewIntervalMet();
        }
        else {
            // Clear inview refresh timer if it exists
            if (this.refresh.inview_interval_timer !== null) {
                clearTimeout(this.refresh.inview_interval_timer);
                this.refresh.inview_interval_timer = null;
            }
            // Set current inview interval to inview interval if it is not set
            this.refresh.current_inview_interval = this.refresh.current_inview_interval || this.refresh.inview_interval;
            // Set current interval
            this.refresh.current_inview_interval = Math.max(this.refresh.current_inview_interval - this.viewability.time_inview_since_displayed, 0);
            if (this.refresh.current_inview_interval > 0) {
                // set invew refresh timer
                this.refresh.inview_interval_timer = setTimeout(function () {
                    _this.setInviewIntervalMet();
                }, this.refresh.current_inview_interval);
            }
            else {
                this.setInviewIntervalMet();
            }
        }
        return true;
    };
    Slot.prototype.setInviewIntervalMet = function () {
        this.refresh.inview_interval_met = true;
        if (this.checkIfRefreshIsAllowed()) {
            // refresh slot
            this.refreshSlot();
        }
    };
    Slot.prototype.handleViewabilityChange = function (isInView) {
        var _a, _b, _c;
        // Check if viewability has changed
        if (this.viewability.inview === isInView)
            return;
        // Set inView
        this.viewability.inview = isInView;
        // Perform actions when the element is in view
        (0, log_1.logInfo)("Element ".concat(this.name, ",  inview: ").concat(isInView));
        // Check if the element is in view
        if (isInView) {
            // Check if slot is displayed and bid is displayed
            if (this.displayed && ((_a = this.displayedBid) === null || _a === void 0 ? void 0 : _a.displayed)) {
                // Set entered inview timestamp
                this.viewability.entered_inview_ts = Date.now();
                // set inview refresh timer if allowed
                this.setInviewRefreshTimer();
                // set viewability timer
                this.displayedBid.setViewabilityTimer();
            }
            // check if video player is paused
            if (this.videoPlayer &&
                (!this.videoPlayer.ad_started ||
                    this.videoPlayer.ad_paused)) {
                // play ads
                this.videoPlayer.playAds();
            }
        }
        else { // Element is out of view
            // Check if slot is displayed and bid is displayed
            if (this.displayed && ((_b = this.displayedBid) === null || _b === void 0 ? void 0 : _b.displayed)) {
                if (this.refresh.inview_interval !== null) {
                    clearTimeout(this.refresh.inview_interval_timer);
                    this.refresh.inview_interval_timer = null;
                }
                // set time since slot was displayed
                this.setTimeInviewSinceDisplayed();
                // clear viewability timer
                (_c = this.displayedBid) === null || _c === void 0 ? void 0 : _c.clearViewabilityTimer();
            }
            // check if video player is paused
            if (this.videoPlayer &&
                this.videoPlayer.ad_started &&
                !this.videoPlayer.ad_paused) {
                // pause ads
                this.videoPlayer.pauseAds();
            }
        }
    };
    /**
     * @returns void
     * @description Set time since slot was displayed
     */
    Slot.prototype.setTimeInviewSinceDisplayed = function () {
        // Get time since slot was displayed
        this.viewability.time_inview_since_displayed = this.viewability.time_inview_since_displayed || 0;
        if (this.viewability.entered_inview_ts) {
            var entered_inview_ts = Math.max(this.viewability.entered_inview_ts, this.displayed_ts);
            this.viewability.time_inview_since_displayed += Math.max(Date.now() - entered_inview_ts, 0);
        }
    };
    /**
     * Insert video div into element
     * @returns boolean
     */
    Slot.prototype.insertOutstreamPlayer = function () {
        var _this = this;
        try {
            // Create video instance
            var video_instance = new VideoInstance_1.VideoInstance(this.div_id, this, function (success, deleteSlot) {
                if (deleteSlot) {
                    _this.removeSlot();
                }
                else if (success) {
                    delete _this.videoPlayer;
                    _this.videoPlayer = null;
                    // Refresh slot
                    _this.refreshSlot();
                }
                else if (!success) {
                    // Reset flags
                    _this.displayed = false;
                    _this.ad_injected = false;
                    // Remove video player
                    delete _this.videoPlayer;
                    _this.videoPlayer = null;
                    // Display winning bid
                    _this.displayWinningBid();
                }
            });
            this.videoPlayer = video_instance;
            return true;
        }
        catch (error) {
            (0, log_1.logError)("Unable to insert outstream player into slot.", this.name, error);
            return false;
        }
    };
    /**
     * @returns void
     * @description Remove expired bids
     */
    Slot.prototype.removeExpiredBids = function () {
        var i = 0;
        while (i < this.bids.length) {
            // Check if the element is expired
            if (this.bids[i].isExpired()) {
                // Remove the element from the array
                this.bids.splice(i, 1);
            }
            else {
                // Increment the index only if no element is removed
                i++;
            }
        }
    };
    /**
     * @returns void
     * @description Remove slot from page
    */
    Slot.prototype.removeSlot = function () {
        // remove slot from page
        this.element.remove();
        // remove viewability tracker
        this.removeViewabilityTracker();
        // remove lazyload trackers
        this.removeLazyloadTrackers();
        // remove lazyload trackers
        this.removeAutoDeleteTracker();
        // remove slot from page
        delete Page_1.globalPage.slots[this.name];
    };
    return Slot;
}());
exports.default = Slot;
